import type { ComponentProps } from "react";
import { StoryblokComponent, storyblokEditable } from "@storyblok/react/rsc";

import { Hero as HeroComponent } from "@/components/layout/Hero";
import { Animation } from "@/components/media/Animation";
import { GridContainer } from "@/components/layout/GridContainer";
import { RichTextField } from "@/components/typography/RichTextField";
import type { HeroColor } from "@/components/layout/Hero";
import type { HeroStoryblok } from "@/component-types-sb";
import { cn } from "@/lib/utils";

const heroBgClasses: Record<string, string> = {
  "": "bg-sand-50",
  "none": "bg-transparent",
  "sand-50": "bg-sand-50",
  "sand-100": "bg-sand-100",
  "sky-50": "bg-sky-50",
  "sun-100": "bg-sun-100",
  "white": "bg-white",
};

type HeroProps = ComponentProps<"section"> & {
  blok: HeroStoryblok;
};

const Hero = ({ blok, ...rest }: HeroProps) => {
  if (blok.variant === "secondary") {
    return (
      <HeroComponent
        headline={blok.headline}
        cta={blok.cta?.[0]}
        shape="shape2"
        color={blok.color as HeroColor}
        className="break-container"
      />
    );
  }

  const bgClass = heroBgClasses[blok.background_color || ""] || "bg-sand-50";

  return (
    <section
      className={cn(
        bgClass,
        "pb-8 break-container",
        bgClass !== "bg-transparent" && "rounded-b-[32px]",
        blok.animated ? "md:pt-8" : "pt-24 md:pt-48 pb-16 px-4 md:px-14",
        rest.className,
      )}
      {...storyblokEditable(blok)}
    >
      {blok.animated && (
        <div className="mb-4 sm:mb-8 md:mb-6 lg:mb-8">
          <Animation />
        </div>
      )}

      <GridContainer className="lg:!px-0">
        <div className="col-span-12 lg:col-span-10 lg:col-start-2">
          {blok.subline && (
            <p className="text-copy-sm md:text-copy font-medium text-gray-100 mb-2 md:mb-3">
              {blok.subline}
            </p>
          )}
          <h1 className="text-2.5xl md:text-7xl  mb-4 md:mb-8 md:max-w-4xl leading-tight">
            {blok.headline}
          </h1>

          {blok.text && (
            <RichTextField value={blok.text} className="text-copy md:text-3xl md:max-w-4xl" />
          )}

          {blok.cta && blok.cta.length > 0 && (
            <div className="flex flex-col md:flex-row gap-4 md:items-center md:h-16 mt-8 [&>*]:w-full [&>*>*]:w-full md:[&>*]:w-auto md:[&>*>*]:w-auto [&>*]:text-center md:[&>*]:text-left">
              {blok.cta.map((nestedBlok: any) => (
                <StoryblokComponent blok={nestedBlok} key={nestedBlok._uid} />
              ))}
            </div>
          )}
        </div>
      </GridContainer>
    </section>
  );
};

export default Hero;
